import {
  Dictionary,
  every,
  filter,
  flatMap,
  forEach,
  get,
  groupBy,
  includes,
  isEmpty,
  isNumber,
  keyBy,
  keys,
  map,
  pick,
  size,
  some,
  uniq,
  values,
} from 'lodash';

import { modelTypeClassification } from 'components/Pages/Jobs/inferenceFieldsOptions';
import GridBasedCalculationParams from 'interfaces/girdBasedCalculationParams';
import { AttributesTable } from 'interfaces/inferenceArtifacts';
import PostProcessingAction, {
  InputSource,
  InputType,
  MappingFilterMetadata,
  PostProcessingActionCreated,
} from 'interfaces/postProcessingAction';
import { VisualizationCreated } from 'interfaces/visualization';
import { AnnotationAssignmentUsage, AssignmentByStain } from 'utils/assignmentManagement';
import { OrchestrationBySlideByResultName, OrchestrationBySlideByType } from 'utils/orchestrationSelections';

import type { ClassConfigs } from './classConfigFromSelections';
import { isGridBasedCalculationParamsValid } from './GridBasedCalculationParamsStep';
import { getSidesWithoutSomeModels, getSlidesModelsData } from './InferenceModelsForSlides/utils';
import { getRelevantSelectedStainsForInput } from './PostProcessingActions/PostProcessingAction';
import {
  areInstanceCountThresholdsValid,
  getInstanceCountThresholdOptions,
  INSTANCE_COUNT_POSITIVITY_ACTION_NAME,
} from './PostProcessingActions/instanceCountPositivity';
import { getInputType, validateInput } from './PostProcessingActions/utils';

export function isInferenceResultsStepValid(
  slides: any[],
  selectedOrchestrations: OrchestrationBySlideByType,
  allSelectedNonModelOrchestrations: OrchestrationBySlideByResultName[],
  selectedAssignments: AssignmentByStain,
  relevantModelTypes: string[],
  selectedSlideRegistrations: any
): boolean {
  // Check if selected the same model type for each stain
  const slidesById = keyBy(slides, 'slideId');
  const slidesModels = getSlidesModelsData(
    slidesById,
    selectedOrchestrations,
    allSelectedNonModelOrchestrations,
    selectedAssignments,
    relevantModelTypes,
    selectedSlideRegistrations
  );
  const slidesWithoutSomeModels = getSidesWithoutSomeModels(relevantModelTypes, slidesModels);

  // Check if selected classification model for classifications orchestrations
  const modelClassificationsFromClassificationOptions = every(selectedOrchestrations, (selectedOrchestration) =>
    every(selectedOrchestration, (orchestrationData, modelType) => modelType !== modelTypeClassification.apiModelValue)
  );

  // Check if selected annotations assignments have duplicate model types per stain
  const isDuplicateModelTypesPerStain = some(selectedAssignments, (selectedAssignmentsByStain) => {
    // remove assignments without model type
    const selectedAssignmentsByStainWithoutModelType = filter(
      selectedAssignmentsByStain,
      (assignment) => !isEmpty(assignment.modelType)
    );

    return some(
      groupBy(selectedAssignmentsByStainWithoutModelType, 'modelType'),
      (assignments) => size(assignments) > 1
    );
  });

  const isSelectedOrchestrations = some(
    values(selectedOrchestrations),
    (selectedOrchestration) => !isEmpty(selectedOrchestration)
  );

  const isSelectedNonModelOrchestrations = some(
    values(allSelectedNonModelOrchestrations),
    (selectedNonModelOrchestration) => !isEmpty(selectedNonModelOrchestration)
  );

  const isSelectedAssignments = some(
    flatMap(values(selectedAssignments)),
    (selectedAssignment) => !isEmpty(selectedAssignment)
  );

  const isSelectedAssignmentsValid = every(
    flatMap(values(selectedAssignments)),
    (selectedAssignment) =>
      !isEmpty(selectedAssignment.assignment) &&
      (selectedAssignment.annotationAssignmentUsage !== AnnotationAssignmentUsage.ManualCorrection ||
        !isEmpty(selectedAssignment.modelType))
  );

  return (
    isEmpty(slidesWithoutSomeModels) &&
    modelClassificationsFromClassificationOptions &&
    !isDuplicateModelTypesPerStain &&
    (isSelectedAssignments ? isSelectedAssignmentsValid : true) &&
    (isSelectedOrchestrations || isSelectedAssignments || isSelectedNonModelOrchestrations)
  );
}

export function isVisualizationStepValid(
  visualizations: VisualizationCreated[],
  classConfigWithSpecialClasses: ClassConfigs,
  postProcessingClasses: ClassConfigs
): boolean {
  const visualizationUniqIds = uniq(map(visualizations, (visualization) => pick(visualization, ['id', 'stain'])));

  if (visualizationUniqIds.length !== visualizations.length) {
    return false;
  }
  let isValid = true;
  forEach(visualizations, (visualization) => {
    const isClassification = visualization.id === 'classification';
    forEach(visualization?.layers, (layer) => {
      if (
        isEmpty(layer?.classKey) ||
        (!includes(
          classConfigWithSpecialClasses[visualization.stain]?.[visualization.input] as string[],
          layer.classKey
        ) &&
          !includes(
            (postProcessingClasses[visualization.stain]?.[visualization.input] as string[]) || [],
            layer.classKey
          )) ||
        (isClassification &&
          !isNumber(layer.classificationClassKey) &&
          isEmpty(layer.classificationClassKey) &&
          !includes(
            classConfigWithSpecialClasses[visualization.stain]?.[visualization.readAs] || [],
            layer.classificationClassKey as string | number
          ))
      ) {
        isValid = false;
        return false;
      }
    });
  });

  return isValid;
}

export function isGridParamsStepValid(
  gridBasedFeaturesStains: string[],
  gridBasedCalculationParams: Record<string, GridBasedCalculationParams>
): boolean {
  return (
    gridBasedFeaturesStains.length === keys(gridBasedCalculationParams).length &&
    every(gridBasedCalculationParams, (params) => isGridBasedCalculationParamsValid(params))
  );
}

export const arePostProcessingActionsValid = ({
  actions,
  indexForOptions,
  actionsForOptions = actions,
  postProcessingActionsById,
  getOptions,
  getMappingFiltersMetadataForLogicalQuery,
}: {
  actions: PostProcessingActionCreated[];
  postProcessingActionsById: Dictionary<PostProcessingAction>;
  indexForOptions?: number;
  actionsForOptions?: PostProcessingActionCreated[];
  getOptions: (
    optionSource: string | string[],
    selectedStains: string[],
    index: number,
    actions: PostProcessingActionCreated[],
    actionInput: string,
    postProcessingActionsById: Dictionary<PostProcessingAction>,
    inputSourceDependentOn?: string
  ) => {
    id: string;
    label: string;
  }[];
  getMappingFiltersMetadataForLogicalQuery: (
    stain: string,
    index: number,
    actions: PostProcessingActionCreated[],
    actionInput: string,
    postProcessingActionsById: Dictionary<PostProcessingAction>,
    actionAttributesTable: AttributesTable
  ) => MappingFilterMetadata[];
}) => {
  let isValid = true;

  forEach(actions, (action, index) => {
    const actionDefinition = postProcessingActionsById[action.actionId];
    if (isEmpty(actionDefinition)) {
      isValid = false;
      return false;
    }

    const optionsIndex = indexForOptions ?? index;
    if (action.actionName === INSTANCE_COUNT_POSITIVITY_ACTION_NAME) {
      const selectedStains = action.stain ? [action.stain] : action.stains ?? [];
      const thresholdOptions = getInstanceCountThresholdOptions(
        getOptions(
          InputSource.INSTANCE_COUNT_THRESHOLDS,
          selectedStains,
          optionsIndex,
          actionsForOptions,
          action.actionInput,
          postProcessingActionsById
        )
      );
      if (!areInstanceCountThresholdsValid(action, thresholdOptions)) {
        isValid = false;
        return false;
      }
    }

    forEach(actionDefinition.inputs, (input) => {
      const inputType = getInputType(input, action);
      const selectedStains = getRelevantSelectedStainsForInput(input, action);
      const options =
        inputType === InputType.LOGICAL_QUERY
          ? getMappingFiltersMetadataForLogicalQuery(
              action?.stain,
              index,
              actionsForOptions,
              action?.actionInput,
              postProcessingActionsById,
              input?.attributesTable
            )
          : getOptions(
              input.inputSource,
              selectedStains,
              optionsIndex,
              actionsForOptions,
              action.actionInput,
              postProcessingActionsById,
              get(action, input.inputSourceDependentOn)
            );
      if (!validateInput(get(action, input.inputKey), input, action, options)) {
        isValid = false;
        return false;
      }
    });
  });

  return isValid;
};
